// Api.
import api from '../Base';
import archiver from '../Archiver';

// Types.
import HostTarget from '../../types/host/HostTarget'; // Keep existing if used elsewhere
import HostInstances from "../../types/host/HostInstances"; // Keep existing if used elsewhere
import HostStatistic from '../../types/host/HostStatistic';
import HostTimeStatistic from '../../types/host/HostTimeStatistic';
import HostStatisticV2 from '../../types/host/HostStatisticV2';
import { showError } from '../../utils/showAlert';

// Placeholder type for datasource-host mapping
export interface DatasourceHostMap {
    id: number; // Corrected field name from datasource_id
    hostid: number; // Renamed from host_id to match log output
    // Add other relevant fields if known
}

// Removed HostDetail interface

const HostApi = {

    getHosts:async (): Promise<HostTarget[]> => { // Revert return type to HostTarget[]

        let url = `host?sort=2+asc`;

        let hosts: HostTarget[] = []; // Revert type

        await api.get<HostTarget[]>(url) // Revert type
            .then((response) => {
                hosts = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve hosts.', error);
                showError('Failed to retrieve hosts', error.message || error.toString());
            });

        return hosts;
    },

    // Add the new function here
    getDatasourceHostMapping: async (): Promise<DatasourceHostMap[]> => {
        const url = `datasource/host`;
        let mapping: DatasourceHostMap[] = [];

        await api.get<DatasourceHostMap[]>(url)
            .then((response) => {
                mapping = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve datasource-host mapping.', error);
                showError('Failed to retrieve datasource-host mapping', error.message || error.toString());
                // Optionally re-throw or return empty array based on desired error handling
            });

        return mapping;
    },


    getHostTypes:async () => {

        let url = `host/type?collect=true`;

        let hosts: any[] = [];

        await api.get<any[]>(url)
            .then((response) => {
                hosts = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve hosts.', error);
                showError('Failed to retrieve hosts', error.message || error.toString());
            });

        return hosts;
    },

    getHostStatusTypes:async () => {

        let url = `host/status/type?collect=true`;

        let types: any[] = [];

        await api.get<any[]>(url)
            .then((response) => {
                types = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instances.', error);
                showError('Failed to retrieve instances', error.message || error.toString());
            });

        return types;
    },

    getHostStatisticTypes:async () => {

        let url = `host/statistic/type?collect=true`;

        let types: any[] = [];

        await api.get<any[]>(url)
            .then((response) => {
                types = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instances.', error);
                showError('Failed to retrieve instances', error.message || error.toString());
            });

        return types;
    },

    
    getHost:(hostId: number) => {

        let url = `host?sort=2+asc&id=${hostId}`;

        api.get<HostTarget[]>(url)
            .then((response) => {
                return response.data[0];
            })
            .catch((error: any) => {
                console.error('Failed to retrieve host.', error);
                showError('Failed to retrieve host', error.message || error.toString());
            });
    },

    getHostInstances:async (id: number) => {

        let url = `datasource/host?hostid=${id}`;

        let instances: HostInstances[] = [];

        await api.get<HostInstances[]>(url)
            .then((response) => {
                instances = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve hosts.', error);
                showError('Failed to retrieve hosts', error.message || error.toString());
            });

        return instances;
    },

    getHostStatisticSummary: async (
        timeRangeQueryString: string,
        statistic: string,
        hostId?: number
    ): Promise<HostStatistic[]> => {
        const idParam = hostId !== undefined ? `&id=${hostId}` : '';
        const url = `host/statistic/summary?${timeRangeQueryString}&statistic=${encodeURIComponent(statistic)}${idParam}`;
        let data: HostStatistic[] = [];

        await api.get<HostStatistic[]>(url)
            .then((response) => {
                data = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve host statistic summary.', error);
                showError('Failed to retrieve host statistic summary', error.message || error.toString());
            });

        return data;
    },

    getHostStatisticTime: async (
        timeRangeQueryString: string,
        hostId: number,
        statistic: string,
        limit?: number
    ): Promise<HostTimeStatistic[]> => {
        const limitParam = limit !== undefined ? `&limit=${limit}` : '';
        const url = `host/statistic/time?${timeRangeQueryString}&statistic=${encodeURIComponent(statistic)}&id=${hostId}${limitParam}`;
        let data: HostTimeStatistic[] = [];

        await api.get<HostTimeStatistic[]>(url)
            .then((response) => {
                data = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve host statistic time.', error);
                showError('Failed to retrieve host statistic time', error.message || error.toString());
            });

        return data;
    },

    /**
     * v2 host statistic summary — returns every metric in a single row.
     * Pass `hostIds` to restrict to specific hosts (uses repeated `id=` params); omit for an org-wide summary.
     * One call replaces N per-statistic calls.
     */
    getHostStatisticV2Summary: async (
        timeRangeQueryString: string,
        hostIds?: number[]
    ): Promise<HostStatisticV2[]> => {
        const idParams = hostIds && hostIds.length > 0
            ? '&' + hostIds.map(id => `id=${id}`).join('&')
            : '';
        const url = `/rest/v2/host/statistic?${timeRangeQueryString}${idParams}`;
        try {
            const response = await archiver.get<HostStatisticV2[]>(url);
            return response.data || [];
        } catch (error: any) {
            console.error('Failed to retrieve v2 host statistic summary.', error);
            showError('Failed to retrieve host statistic summary', error.message || error.toString());
            return [];
        }
    },

    /**
     * v2 host statistic per host — returns one row per supplied host id with every metric included.
     *
     * The v2 endpoint aggregates across all matching hosts in a single call, so to get a per-host
     * breakdown we fan out one parallel summary call per host id. This is still a huge improvement
     * over the legacy v1 flow (which needed one call per host PER STATISTIC, so 4N calls for the
     * host list); here it is N parallel calls and each call returns every metric we need.
     *
     * If `hostIds` is omitted or empty, returns an empty array.
     */
    getHostStatisticV2ById: async (
        timeRangeQueryString: string,
        hostIds?: number[]
    ): Promise<HostStatisticV2[]> => {
        if (!hostIds || hostIds.length === 0) {
            return [];
        }
        try {
            const responses = await Promise.all(
                hostIds.map(id =>
                    archiver.get<HostStatisticV2[]>(`/rest/v2/host/statistic?${timeRangeQueryString}&id=${id}`)
                        .then(response => ({ id, row: response.data?.[0] }))
                )
            );
            return responses
                .filter(r => r.row)
                .map(r => ({ ...(r.row as HostStatisticV2), id: r.id }));
        } catch (error: any) {
            console.error('Failed to retrieve v2 host statistic by id.', error);
            showError('Failed to retrieve host statistic by id', error.message || error.toString());
            return [];
        }
    },

    /**
     * v2 host statistic time series — every metric per timeslice in a single response.
     * Pass `hostIds` to restrict to specific hosts (results are aggregated across the provided hosts).
     */
    getHostStatisticV2Time: async (
        timeRangeQueryString: string,
        hostIds?: number[],
        limit?: number
    ): Promise<HostStatisticV2[]> => {
        const idParams = hostIds && hostIds.length > 0
            ? '&' + hostIds.map(id => `id=${id}`).join('&')
            : '';
        const limitParam = limit !== undefined ? `&limit=${limit}` : '';
        const url = `/rest/v2/host/statistic/time?${timeRangeQueryString}${idParams}${limitParam}`;
        try {
            const response = await archiver.get<HostStatisticV2[]>(url);
            return response.data || [];
        } catch (error: any) {
            console.error('Failed to retrieve v2 host statistic time.', error);
            showError('Failed to retrieve host statistic time', error.message || error.toString());
            return [];
        }
    },
}

export default HostApi;
